/**
 * Licensing rules.
 *
 * Seat semantics, decided by the owner:
 *
 *   0      — no seats. The application is NOT available, even if the module is
 *            switched on. Nobody can be assigned to it and it does not appear as a
 *            usable tile.
 *   1-9998 — that many concurrent named users.
 *   9999   — unlimited. A sentinel rather than NULL, so "unlimited" is something an
 *            administrator can type into the seat box like any other number.
 */

export const UNLIMITED_SEATS = 9999;

export function isUnlimitedSeats(seatLimit: number): boolean {
  return seatLimit >= UNLIMITED_SEATS;
}

/** A module with zero seats is unavailable regardless of the enabled flag. */
export function hasAvailableSeats(seatLimit: number): boolean {
  return seatLimit > 0;
}

/** Seats left, or null when unlimited. */
export function seatsRemaining(seatLimit: number, seatsUsed: number): number | null {
  if (isUnlimitedSeats(seatLimit)) return null;
  return Math.max(0, seatLimit - seatsUsed);
}

export function describeSeats(seatLimit: number, seatsUsed: number): string {
  if (seatLimit === 0) return 'No seats — not available';
  if (isUnlimitedSeats(seatLimit)) return `${seatsUsed} assigned · unlimited`;
  return `${seatsUsed} of ${seatLimit} seats used`;
}

// ---------------------------------------------------------------------------
// Temporary licences
// ---------------------------------------------------------------------------

export type LicenseType = 'permanent' | 'temporary';

/**
 * A temporary licence runs for exactly five business days from the moment it is
 * granted. Fixed by the platform on purpose — if this were per-grant, a "temporary"
 * licence could be handed out for a year and the concept would mean nothing.
 */
export const TEMPORARY_LICENSE_BUSINESS_DAYS = 5;

/**
 * Adds business days to a date, skipping weekends.
 *
 * Public holidays are NOT modelled: the platform's holiday-aware calendar is
 * `businessDayCalendar`, which is lifted from AI Readiness in Phase 1. This
 * deliberately matches only the weekend rule until that engine lands, so it can be
 * swapped for the real calendar in one place rather than diverging from it.
 */
export function addBusinessDays(from: Date, days: number): Date {
  const result = new Date(from.getTime());
  let remaining = days;
  while (remaining > 0) {
    result.setDate(result.getDate() + 1);
    const day = result.getDay();
    if (day !== 0 && day !== 6) remaining -= 1;
  }
  return result;
}

export type TemporaryLicenseState =
  /** Inside its five-business-day window. */
  | 'active'
  /** The window has passed; access is withdrawn. */
  | 'expired';

export interface TemporaryLicense {
  /** Set at grant time — five business days after granted_at. */
  expiresAt: Date | null;
}

export function temporaryLicenseState(
  license: TemporaryLicense,
  now: Date = new Date(),
): TemporaryLicenseState {
  if (!license.expiresAt) return 'expired';
  return license.expiresAt.getTime() > now.getTime() ? 'active' : 'expired';
}

/** Expiry for a licence granted now: five business days from the grant. */
export function temporaryLicenseExpiry(grantedAt: Date = new Date()): Date {
  return addBusinessDays(grantedAt, TEMPORARY_LICENSE_BUSINESS_DAYS);
}

/** Business days left before a temporary licence lapses; 0 once expired. */
export function businessDaysRemaining(expiresAt: Date, now: Date = new Date()): number {
  if (expiresAt.getTime() <= now.getTime()) return 0;
  let count = 0;
  const cursor = new Date(now.getTime());
  while (cursor.getTime() < expiresAt.getTime()) {
    cursor.setDate(cursor.getDate() + 1);
    const day = cursor.getDay();
    if (day !== 0 && day !== 6) count += 1;
  }
  return count;
}
